import {Box, Checkbox, FormControlLabel, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon';
import {AllowedCountryCodesTeens} from '@/app/auth/constants/AllowedPhoneNumbers';
import PrivacyPolicy from '@/app/main/onboarding/components/PrivacyPolicy/PrivacyPolicy';
import {
  AppButton,
  ButtonContainer,
  PhoneInputTextField,
} from '@/app/shared-components';
import ChangeTextWithColorAndImage from '@/app/shared-components/ColorAndImageText/ColorAndImageText';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import SecureEncryptFooter from '@/app/shared-components/SecureEncryptFooter/SecureEncryptFooter';
import {COLORS} from '@/styles/colors';

import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

import './styles';

const ContactInfoContent = () => {
  const {t} = useTranslation();
  const {
    phone,
    email,
    kidFirstName,
    isDisabled,
    handleContinue,
    handleBack,
    setEmail,
    setPhone,
    isEmailEqualToParent,
    phoneInputErrorMessage,
    handleOnChangeCheckbox,
    isCheckedCheckbox,
    showAccountSwitchingForm,
    setEmailError,
    isCountryCodeVisible,
  } = useViewModelProvider();

  return (
    <Box className="contact-info-container">
      <div className="contact-info-back-icon">
        <FuseSvgIcon
          size={16}
          sx={{cursor: 'pointer'}}
          onClick={handleBack}>
          heroicons-outline:arrow-left
        </FuseSvgIcon>
      </div>

      <Box className="contact-info-content">
        <Box className="contact-info-title-container">
          <ChangeTextWithColorAndImage
            text={t('add_kid.contact_info.title', {
              name: kidFirstName,
            })}
            textStyle={{
              color: COLORS.ON_SURFACE,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: -0.22,
            }}
          />
          {!showAccountSwitchingForm && (
            <Box>
              <Typography className="contact-info-subtitle">
                {t('add_kid.contact_info.subtitle_1', {name: kidFirstName})}
              </Typography>
              <Typography className="contact-info-subtitle contact-info-subtitle-light">
                {t('add_kid.contact_info.subtitle_2')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box className="w-full flex flex-col gap-20 text-left">
          {showAccountSwitchingForm && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCheckedCheckbox}
                  onChange={handleOnChangeCheckbox}
                  sx={{
                    '&.Mui-checked': {
                      color: COLORS.ON_SURFACE_1,
                    },
                  }}
                />
              }
              label={t('add_kid.contact_info.checkbox_label', {
                name: kidFirstName,
              })}
              sx={{
                '& .MuiFormControlLabel-label': {
                  textAlign: 'left',
                },
              }}
            />
          )}

          {!isCheckedCheckbox && (
            <>
              <section>
                <InputTextField
                  id="add-kid-contact-info-email-input"
                  disabled={isCheckedCheckbox}
                  autoComplete="email"
                  type="email"
                  label={t('add_kid.contact_info.email')}
                  placeholder={t('add_kid.contact_info.email')}
                  value={email}
                  setValue={setEmail}
                  error={isEmailEqualToParent}
                  autofocus
                  onErrorChange={setEmailError}
                />
                {isEmailEqualToParent && (
                  <Typography className="email-already-exists">
                    {t('add_kid.contact_info.invalid_email_equal_parent')}
                  </Typography>
                )}
              </section>
              <PhoneInputTextField
                id="add-kid-contact-info-phone-input"
                isDisabled={isCheckedCheckbox}
                label={t('add_kid.contact_info.phone')}
                value={phone}
                onChange={setPhone}
                countries={AllowedCountryCodesTeens}
                customError={phoneInputErrorMessage}
                showCountryCode={isCountryCodeVisible}
              />
            </>
          )}
        </Box>
        {isCheckedCheckbox ? (
          <Box className="flex flex-col items-center gap-[8px] self-stretch">
            <Box
              component="img"
              src="/assets/images/etc/switch-account-preview.webp"
              alt="Switch Account Preview"
              width={286}
              height={241}
            />
            <Box className="flex flex-col items-center gap-[12px] self-stretch">
              <Typography className="contact-info-switch-title">
                {t('add_kid.contact_info.switch_preview.title')}
              </Typography>
              <Typography className="contact-info-switch-subtitle">
                {t('add_kid.contact_info.switch_preview.subtitle', {
                  name: kidFirstName,
                })}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className="flex flex-col gap-4">
            <SecureEncryptFooter />
            <Box style={{marginLeft: '4px'}}>
              <PrivacyPolicy />
            </Box>
          </Box>
        )}
      </Box>
      <ButtonContainer sticky>
        <AppButton
          id="add-kid-contact-info-continue-button"
          disabled={isDisabled}
          label={t('add_kid.contact_info.continue')}
          onClick={handleContinue}
        />
      </ButtonContainer>
    </Box>
  );
};
const ContactInfoPage = () => {
  return (
    <ViewModelProvider>
      <ContactInfoContent />
    </ViewModelProvider>
  );
};

export default ContactInfoPage;
